import { createFileRoute } from "@tanstack/react-router";
import { CPC_TABLE, CPC_UPDATED_AT, CPC_SOURCES } from "@/lib/cpc-benchmarks";
import { TRADE_LOCAL_ANGLES } from "@/lib/site-data";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from "@/components/ui/chart";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis, Legend } from "recharts";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";

const TITLE = "Average Google Ads CPC for Trades by City (Utah County, 2026)";
const DESCRIPTION =
  "Quarterly cost-per-click benchmarks for plumbing, electrical, HVAC, and roofing contractors across Lehi, American Fork, Saratoga Springs, Pleasant Grove, Eagle Mountain, Orem, and Provo.";
const CANONICAL = "https://lehitradessaeo.com/data/trades-cpc";

export const Route = createFileRoute("/data/trades-cpc")({
  head: () => ({
    meta: [
      { title: `${TITLE} — Lehi Trades SEO` },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: CANONICAL },
      { property: "og:type", content: "article" },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Dataset",
          name: TITLE,
          description: DESCRIPTION,
          url: CANONICAL,
          creator: { "@id": "https://lehitradessaeo.com/#organization" },
          temporalCoverage: CPC_UPDATED_AT,
          variableMeasured: TRADE_LOCAL_ANGLES.map((t) => ({
            "@type": "PropertyValue",
            name: `${t.trade} CPC`,
            unitCode: "USD",
          })),
        }),
      },
    ],
  }),
  component: TradesCpcPage,
});

const chartConfig = {
  plumber: { label: "Plumber", color: "var(--chart-1)" },
  electrician: { label: "Electrician", color: "var(--chart-2)" },
  hvac: { label: "HVAC", color: "var(--chart-3)" },
  roofer: { label: "Roofer", color: "var(--chart-4)" },
} satisfies ChartConfig;

function TradesCpcPage() {
  const chartData = CPC_TABLE.map((row) => ({
    city: row.cityName,
    plumber: row.cpc.plumber,
    electrician: row.cpc.electrician,
    hvac: row.cpc.hvac,
    roofer: row.cpc.roofer,
  }));

  function downloadCsv() {
    const header = ["City", "Region", ...TRADE_LOCAL_ANGLES.map((t) => t.trade)];
    const rows = CPC_TABLE.map((r) => [
      r.cityName,
      r.region,
      ...TRADE_LOCAL_ANGLES.map((t) => r.cpc[t.slug].toFixed(2)),
    ]);
    const csv = [header, ...rows]
      .map((row) =>
        row
          .map((v) => {
            const s = String(v);
            return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
          })
          .join(","),
      )
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `trades-cpc-benchmarks-${CPC_UPDATED_AT}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <main className="mx-auto max-w-5xl px-6 py-16">
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
        Open data · Updated {CPC_UPDATED_AT}
      </p>
      <h1 className="mt-3 font-display text-4xl tracking-tight md:text-5xl">{TITLE}</h1>
      <p className="mt-4 text-lg text-muted-foreground">{DESCRIPTION}</p>

      <div className="mt-6">
        <Button onClick={downloadCsv} variant="outline" size="sm">
          <Download aria-hidden="true" />
          Download CSV
        </Button>
      </div>

      <section className="mt-10 rounded-2xl border border-border bg-card p-6">
        <h2 className="font-display text-xl tracking-tight">CPC by city &amp; trade</h2>
        <ChartContainer config={chartConfig} className="mt-4 h-80 w-full">
          <BarChart data={chartData}>
            <CartesianGrid vertical={false} strokeDasharray="3 3" />
            <XAxis dataKey="city" tickLine={false} axisLine={false} fontSize={12} />
            <YAxis tickLine={false} axisLine={false} fontSize={12} tickFormatter={(v) => `$${v}`} />
            <ChartTooltip content={<ChartTooltipContent />} />
            <Legend />
            <Bar dataKey="plumber" fill="var(--color-plumber)" radius={[4, 4, 0, 0]} />
            <Bar dataKey="electrician" fill="var(--color-electrician)" radius={[4, 4, 0, 0]} />
            <Bar dataKey="hvac" fill="var(--color-hvac)" radius={[4, 4, 0, 0]} />
            <Bar dataKey="roofer" fill="var(--color-roofer)" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ChartContainer>
      </section>

      <section className="mt-10">
        <h2 className="font-display text-2xl tracking-tight">Full benchmark table</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          All values are USD, blended Google Search residential service intent.
        </p>
        <div className="mt-4 overflow-x-auto rounded-xl border border-border">
          <Table>
            <TableCaption>
              Average CPC (USD) by trade and city. Source data refreshed {CPC_UPDATED_AT}.
            </TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead>City</TableHead>
                {TRADE_LOCAL_ANGLES.map((t) => (
                  <TableHead key={t.slug} className="text-right">
                    {t.trade.replace(/^\w/, (c) => c.toUpperCase())}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {CPC_TABLE.map((row) => (
                <TableRow key={row.citySlug}>
                  <TableCell className="font-medium">
                    {row.cityName}, {row.region}
                  </TableCell>
                  {TRADE_LOCAL_ANGLES.map((t) => (
                    <TableCell key={t.slug} className="text-right tabular-nums">
                      ${row.cpc[t.slug].toFixed(2)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </section>

      <section className="mt-10 rounded-2xl border border-border bg-card p-6">
        <h3 className="font-display text-lg tracking-tight">Methodology &amp; sources</h3>
        <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
          {CPC_SOURCES.map((s) => (
            <li key={s}>{s}</li>
          ))}
        </ul>
        <p className="mt-3 text-xs text-muted-foreground">
          Benchmarks are directional. Actual CPCs vary with quality score,
          conversion rate, and seasonality. Last refresh: {CPC_UPDATED_AT}.
        </p>
      </section>
    </main>
  );
}